import React, { createContext, useContext, useState, useMemo } from 'react';
import * as z from 'zod';

export const productSchema = (t: (key: string) => string) =>
  z.object({
    product_id: z.number(),
    quantity: z.coerce.number().positive({
      message: t('errors.quantity_positive'),
    }),
    reason_id: z.number().optional(),
  });

export const returnFormTrackingStepSchema = (t: (key: string) => string) =>
  z.object({
    orders: z.array(z.any()).min(1, t('errors.at_least_one_order')),
  });

export const returnFormProductsStepSchema = (t: (key: string) => string) =>
  z.object({
    data: z.array(productSchema(t)).min(1, t('errors.at_least_one_product')),
  });

export const returnCreateFormSchema = (t: (key: string) => string) =>
  z.object({
    tracking: returnFormTrackingStepSchema(t),
    products: returnFormProductsStepSchema(t),
  });

export type ReturnFormTrackingStepValues = z.infer<ReturnType<typeof returnFormTrackingStepSchema>>;
export type ReturnFormProductsStepValues = z.infer<ReturnType<typeof returnFormProductsStepSchema>>;
export type ReturnFormValues = z.infer<ReturnType<typeof returnCreateFormSchema>>;

export function returnDataToFormValues(): ReturnFormValues {
  return {
    tracking: {
      orders: [],
    },
    products: {
      data: [],
    },
  };
}

interface FormContextType {
  formState: ReturnFormValues;
  setFormState: React.Dispatch<React.SetStateAction<ReturnFormValues>>;
  resetFormState: () => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export const ReturnsFormContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [formState, setFormState] = useState<ReturnFormValues>(() => returnDataToFormValues());

  const resetFormState = () => {
    const initialState = returnDataToFormValues();
    setFormState(initialState);
  };

  const value = useMemo(
    () => ({
      formState,
      setFormState,
      resetFormState,
    }),
    [formState],
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export const useReturnsFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useReturnsFormContext must be used within an ReturnsFormContextProvider');
  }
  return context;
};
